import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit2, Trash2, User, Mail, Shield, Calendar,
  Flame, Trophy, Star, CreditCard, ToggleLeft, ToggleRight,
  CheckCircle, XCircle, Clock, Activity
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import { usersAPI } from '../../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import '../../styles/pages/admin/UserDetailPage.css';
import '../../styles/admin-shared.css';

const UserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingStatus, setTogglingStatus] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const [userRes, statsRes] = await Promise.all([
        usersAPI.getById(id),
        usersAPI.getStats(id, 30).catch(() => ({ data: { data: null } }))
      ]);
      setUser(userRes.data.data);
      setStats(statsRes.data.data);
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
      alert('Erro ao carregar usuário');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!window.confirm(`Deseja ${user.is_active ? 'desativar' : 'ativar'} o usuário ${user.name}?`)) return;
    setTogglingStatus(true);
    try {
      await usersAPI.toggleStatus(id, !user.is_active);
      setUser(prev => ({ ...prev, is_active: !prev.is_active }));
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao alterar status');
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Deseja realmente excluir o usuário ${user.name}? Esta ação não pode ser desfeita.`)) return;
    try {
      await usersAPI.delete(id);
      navigate('/admin/users');
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao excluir usuário');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container"><div className="spinner" /></div>
      </AdminLayout>
    );
  }

  if (!user) return null;

  return (
    <AdminLayout>
      <div className="user-detail-page">
        <div className="page-header">
          <div className="user-detail-heading">
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/admin/users')}>
              <ArrowLeft size={16} />
            </button>
            <div className="user-detail-avatar">{user.name?.charAt(0)?.toUpperCase()}</div>
            <div>
              <h1><User size={22} /> {user.name}</h1>
              <p>{user.email}</p>
            </div>
          </div>
          <div className="user-detail-actions">
            <button
              className={`btn btn-sm ${user.is_active ? 'btn-warning' : 'btn-success'}`}
              onClick={handleToggleStatus}
              disabled={togglingStatus}
            >
              {user.is_active
                ? <><ToggleRight size={15} /> Desativar</>
                : <><ToggleLeft size={15} /> Ativar</>}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/users/${id}/edit`)}>
              <Edit2 size={15} /> Editar
            </button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete}>
              <Trash2 size={15} /> Excluir
            </button>
          </div>
        </div>

        <div className="analytics-cards-grid">
          <AdminCard
            title="Dias de Sobriedade"
            value={stats?.sobriety_days ?? user.sobriety_days ?? 0}
            subtitle="Total acumulado"
            icon={<Flame size={22} />}
            color="green"
          />
          <AdminCard
            title="Sequência Atual"
            value={stats?.current_streak ?? user.current_streak ?? 0}
            subtitle="dias seguidos"
            icon={<Trophy size={22} />}
            color="yellow"
          />
          <AdminCard
            title="Pontos"
            value={stats?.total_points ?? user.points ?? 0}
            subtitle="Total acumulado"
            icon={<Star size={22} />}
            color="purple"
          />
          <AdminCard
            title="Assinatura"
            value={user.subscription ? 'Ativa' : 'Nenhuma'}
            subtitle={user.subscription?.plan_name || 'Plano gratuito'}
            icon={<CreditCard size={22} />}
            color="blue"
          />
        </div>

        <div className="detail-grid">
          <div className="section-card">
            <h2><User size={16} /> Informações</h2>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label"><Mail size={13} /> Email</span>
                <span className="detail-value">{user.email}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label"><Shield size={13} /> Função</span>
                <span className={`badge badge-${user.role?.toLowerCase()}`}>{user.role}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label"><User size={13} /> Tipo de Vício</span>
                <span className="detail-value">{user.addiction_type || 'Não informado'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label"><Calendar size={13} /> Cadastrado em</span>
                <span className="detail-value">
                  {format(new Date(user.created_at), "dd/MM/yyyy", { locale: ptBR })}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label"><Clock size={13} /> Último acesso</span>
                <span className="detail-value">
                  {user.last_login_at
                    ? format(new Date(user.last_login_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                    : 'Nunca'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status</span>
                <span className={`badge badge-${user.is_active ? 'success' : 'danger'}`}>
                  {user.is_active
                    ? <><CheckCircle size={11} /> Ativo</>
                    : <><XCircle size={11} /> Inativo</>}
                </span>
              </div>
            </div>
          </div>

          <div className="section-card">
            <h2><Activity size={16} /> Atividade (30 dias)</h2>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Check-ins</span>
                <span className="detail-value">{stats?.check_ins ?? 0}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Meditações</span>
                <span className="detail-value">{stats?.meditations ?? 0}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Desafios concluídos</span>
                <span className="detail-value">{stats?.challenges_completed ?? 0}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Posts na comunidade</span>
                <span className="detail-value">{stats?.community_posts ?? 0}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Maior sequência</span>
                <span className="detail-value">{stats?.longest_streak ?? 0} dias</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserDetailPage;
